import React, { useState, useEffect } from 'react';
import api from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { Modal, Button, Form } from 'react-bootstrap';

const UsersManagement = () => {
    const [users, setUsers] = useState([]);
    const [communities, setCommunities] = useState([]);
    const [userCommunities, setUserCommunities] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { csrfToken } = useAuth();

    // Modal states
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [editFormData, setEditFormData] = useState({
        email: '',
        role: '',
        display_name: '',
        email_verified: false,
        is_approved: false
    });

    const fetchUsers = async () => {
        try {
            setLoading(true);
            // Remove the /api prefix because apiService.js already adds it
            const response = await api.get('/gl_admin/users');
            setUsers(response.data);

            // Fetch community-user associations to get multi-community data
            try {
                const associationsResponse = await api.get('/gl_admin/community-users');
                const associations = associationsResponse.data || [];

                // Group associations by user_id
                const userCommunitiesMap = {};
                associations.forEach(assoc => {
                    if (!userCommunitiesMap[assoc.user_id]) {
                        userCommunitiesMap[assoc.user_id] = [];
                    }
                    userCommunitiesMap[assoc.user_id].push({
                        community_id: assoc.community_id,
                        community_name: assoc.community_name,
                        role: assoc.role,
                        is_primary: assoc.is_primary
                    });
                });
                setUserCommunities(userCommunitiesMap);
            } catch (assocErr) {
                // Non-critical error, continue without associations
            }

            setLoading(false);
        } catch (err) {
            setError('Failed to fetch users: ' + (err.response?.data?.message || err.message));
            setLoading(false);
        }
    };

    const fetchCommunities = async () => {
        try {
            // Remove the /api prefix because apiService.js already adds it
            const response = await api.get('/communities');
            setCommunities(response.data.communities);
        } catch (err) {
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchCommunities();
    }, []);

    const handleEditClick = (user) => {
        setSelectedUser(user);
        setEditFormData({
            email: user.email,
            role: user.role,
            display_name: user.display_name || '',
            email_verified: user.email_verified || false,
            is_approved: user.is_approved || false
        });
        setShowEditModal(true);
    };

    const handleDeleteClick = async (userId) => {
        if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
            try {
                // Remove the /api prefix because apiService.js already adds it
                await api.delete(`/gl_admin/users/${userId}`, {
                    headers: { 'X-CSRFToken': csrfToken }
                });
                fetchUsers(); // Refresh the user list
            } catch (err) {
                setError('Failed to delete user: ' + (err.response?.data?.message || err.message));
            }
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditFormData({
            ...editFormData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        try {
            // Remove the /api prefix because apiService.js already adds it
            await api.put(`/gl_admin/users/${selectedUser.id}`, editFormData, {
                headers: { 'X-CSRFToken': csrfToken }
            });

            setShowEditModal(false);
            fetchUsers(); // Refresh the user list
        } catch (err) {
            setError('Failed to update user: ' + (err.response?.data?.message || err.message));
        }
    };

    if (loading) {
        return <div className="text-center mt-5"><div className="spinner-border" role="status"></div></div>;
    }

    if (error) {
        return (
            <div>
                <div className="alert alert-danger">{error}</div>
                <button onClick={fetchUsers} className="btn btn-primary">
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div>
            <h2>Users Management</h2>
            <div className="table-responsive">
                <table className="table table-striped table-hover">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Display Name</th>
                            <th>Email</th>
                            <th>Role</th>
                            <th>Communities</th>
                            <th>Email Verified</th>
                            <th>Approved</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(user => {
                            const userComms = userCommunities[user.id] || [];
                            const primaryComm = userComms.find(c => c.is_primary);

                            return (
                                <tr key={user.id}>
                                    <td>{user.id}</td>
                                    <td>{user.display_name || user.email?.split('@')[0]}</td>
                                    <td>{user.email}</td>
                                    <td>{user.role}</td>
                                    <td>
                                        {userComms.length === 0 ? (
                                            <span className="text-muted">None</span>
                                        ) : userComms.length === 1 ? (
                                            <span>
                                                {userComms[0].community_name}
                                                {userComms[0].is_primary && ' (Primary)'}
                                            </span>
                                        ) : (
                                            <div>
                                                {primaryComm && (
                                                    <div>
                                                        <strong>{primaryComm.community_name}</strong> (Primary)
                                                    </div>
                                                )}
                                                <small className="text-muted">
                                                    +{userComms.length - (primaryComm ? 1 : 0)} other{userComms.length > 2 ? 's' : ''}
                                                </small>
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        <span className={`badge ${user.email_verified ? 'bg-success' : 'bg-danger'}`}>
                                            {user.email_verified ? 'Yes' : 'No'}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`badge ${user.is_approved ? 'bg-success' : 'bg-danger'}`}>
                                            {user.is_approved ? 'Yes' : 'No'}
                                        </span>
                                    </td>
                                    <td>
                                        <button
                                            className="btn btn-sm btn-primary me-2"
                                            onClick={() => handleEditClick(user)}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            className="btn btn-sm btn-danger"
                                            onClick={() => handleDeleteClick(user.id)}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Edit User Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit User</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleFormSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={editFormData.email}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Display Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="display_name"
                                value={editFormData.display_name}
                                onChange={handleInputChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Role</Form.Label>
                            <Form.Select
                                name="role"
                                value={editFormData.role}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="user">User</option>
                                <option value="community_admin">Community Admin</option>
                                <option value="admin">Global Admin</option>
                            </Form.Select>
                        </Form.Group>

                        <div className="mb-3">
                            <Form.Label>Communities</Form.Label>
                            <div className="alert alert-info">
                                <small>
                                    To manage this user's community memberships, please use the
                                    <strong> Community-User Associations</strong> tab.
                                </small>
                            </div>
                            {selectedUser && userCommunities[selectedUser.id] && (
                                <div>
                                    {userCommunities[selectedUser.id].map(comm => (
                                        <div key={comm.community_id} className="mb-1">
                                            <span className="badge bg-secondary me-2">
                                                {comm.community_name}
                                                {comm.is_primary && ' (Primary)'}
                                            </span>
                                            <small className="text-muted">Role: {comm.role}</small>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <Form.Group className="mb-3">
                            <Form.Label>Display Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="display_name"
                                value={editFormData.display_name}
                                onChange={handleInputChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label="Email Verified"
                                name="email_verified"
                                checked={editFormData.email_verified}
                                onChange={handleInputChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label="Approved"
                                name="is_approved"
                                checked={editFormData.is_approved}
                                onChange={handleInputChange}
                            />
                        </Form.Group>

                        <div className="d-flex justify-content-end">
                            <Button variant="secondary" onClick={() => setShowEditModal(false)} className="me-2">
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit">
                                Save Changes
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default UsersManagement;